import { CreateNewReasonV3Modal } from '@modules/common/components/CreateNewReasonV3Modal';
import { trackCreateNewReasonEvents } from '@modules/common/components/CreateNewReasonV3Modal/helpers';
import { useShowCreateNewReasonInProgressDialog } from '@modules/common/components/CreateNewReasonV3Modal/hooks';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useFetchReasons } from '@modules/common/hooks';
import {
  useFetchTrackedKeywords,
  useGetFeedbackReasonLabels,
  useGetFeedbackTrackedKeywordLabels,
  useUpdateEditedPredictions,
} from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import { ThemeMode } from '@modules/theme/constants';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { SearchRounded } from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from '@mui/material';
import dayjs from 'dayjs';
import { ManipulationType } from 'generated/graphql';
import { uniqBy } from 'lodash';
import {
  SyntheticEvent,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce } from 'react-use';
import { FeedbackLabel, FeedbackLabelType } from 'v2/core/entities/feedback';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';
import { AddNewTrackedKeyword } from '../AddNewTrackedKeyword';
import { AddPredictionToast } from '../AddPredictions/AddPredictionToast';
import { getFeedbackLabels } from '../FeedbackDetails/utils';
import { ReasonSuggestions } from '../FeedbackDetails/utils/getReasonSuggestionsFromSuggestedReasons';
import { AutoCompletePaper } from './AutcompletePaper';
import { AutoCompleteGroup } from './AutoCompleteGroup';
import { AutoCompleteOption } from './AutoCompleteOption';
import { CreateNewLabelButton } from './CreateNewLabelButton';

interface AddPredictionsAutocompleteProps {
  isOpen: boolean;
  onClose: () => void;
  feedbackID: string;
  onAdditionOfReasonSuggestionProp?: () => void;
  isDetailView: boolean;
  reasonSuggestions: ReasonSuggestions[];
  feedbackLabels: FeedbackLabel[] | undefined;
  areReasonSuggestionsLoading: boolean;
}

export type ReasonOrEntityLabelTypeWithGroupName = ReasonOrEntityLabelType & {
  groupName?: string;
};

const isReasonPartOfSuggestions = (
  option: ReasonOrEntityLabelType,
  suggestions: ReasonSuggestions[],
) => {
  return suggestions.some((suggestion) => suggestion.label.id === option.id);
};

export const AddPredictionsAutocomplete = ({
  isOpen,
  onClose,
  feedbackID,
  onAdditionOfReasonSuggestionProp,
  isDetailView,
  reasonSuggestions,
  feedbackLabels,
  areReasonSuggestionsLoading,
}: AddPredictionsAutocompleteProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectionMode, setSelectionMode] = useState<
    'reasons' | 'trackedKeywords'
  >('reasons');
  const [isAddTrackedKeywordView, setIsAddTrackedKeywordView] = useState(false);
  const [isAddReasonView, setIsAddReasonView] = useState(false);

  const { openModal } = useMuiModalContext();

  const { showSuccessNotification } = useNotificationContext();

  const [selectedReasonDefinition, setSelectedReasonDefinition] =
    useState<string>();
  const [shouldAddReasonToFeedback, setShouldAddReasonToFeedback] =
    useState(false);
  const [
    shouldAddTrackedKeywordToFeedback,
    setShouldAddTrackedKeywordToFeedback,
  ] = useState(false);

  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );

  const getReasonSuggestionLabels = useCallback(
    () => reasonSuggestions.map((s) => s.label),
    [reasonSuggestions],
  );
  const [options, setOptions] = useState<
    ReasonOrEntityLabelTypeWithGroupName[]
  >(getReasonSuggestionLabels);

  useEffect(() => {
    setOptions(getReasonSuggestionLabels());
  }, [getReasonSuggestionLabels]);

  const { entityLabels, reasonLabels } = useMemo(
    () => getFeedbackLabels(feedbackLabels),
    [feedbackLabels],
  );

  const feedbackReasonLabels = useGetFeedbackReasonLabels({
    labels: reasonLabels,
    feedbackID,
  });
  const feedbackTrackedKeywordLabels = useGetFeedbackTrackedKeywordLabels({
    labels: entityLabels,
    feedbackID,
  });

  const feedbackReasonLabelsRef = useRef(feedbackReasonLabels);
  const feedbackTrackedKeywordLabelsRef = useRef(feedbackTrackedKeywordLabels);
  const autocompleteContainerRef = useRef<HTMLDivElement>(null);

  const [
    fetchReasons,
    {
      loading: loadingReasons,
      reasons,
      nextPageNumber: nextPageNumberOfReasons,
    },
  ] = useFetchReasons({
    shouldFetchInitialData: isOpen,
  });

  const [
    fetchTrackedKeywords,
    {
      loading: loadingTrackedKeywords,
      trackedKeywords,
      nextPageNumber: nextPageNumberOfTrackedKeywords,
    },
  ] = useFetchTrackedKeywords({
    shouldFetchInitialData: isOpen,
  });

  const { updateEditedPredictions } = useUpdateEditedPredictions(feedbackID);

  const reasonPageNumberRef = useRef(nextPageNumberOfReasons);
  const trackedKeywordsPageNumberRef = useRef(nextPageNumberOfTrackedKeywords);
  const loadingRef = useRef(loadingReasons || loadingTrackedKeywords);

  useEffect(() => {
    if (reasonPageNumberRef.current !== nextPageNumberOfReasons) {
      reasonPageNumberRef.current = nextPageNumberOfReasons;
    }
  }, [nextPageNumberOfReasons]);

  useEffect(() => {
    if (
      trackedKeywordsPageNumberRef.current !== nextPageNumberOfTrackedKeywords
    ) {
      trackedKeywordsPageNumberRef.current = nextPageNumberOfTrackedKeywords;
    }
  }, [nextPageNumberOfTrackedKeywords]);

  useEffect(() => {
    loadingRef.current = loadingReasons || loadingTrackedKeywords;
  }, [loadingReasons, loadingTrackedKeywords]);

  useEffect(() => {
    if (selectionMode === 'trackedKeywords') {
      const trackedKeywordsWithGroup: ReasonOrEntityLabelTypeWithGroupName[] =
        trackedKeywords.map((trackedKeyword) => ({
          ...trackedKeyword,
          groupName: ' All Tracked Keywords',
        }));

      // filter out reason suggestions that are already added
      const filteredOptions = trackedKeywordsWithGroup.filter((entity) =>
        feedbackTrackedKeywordLabelsRef.current.every(
          (trackedKeywordLabel) => trackedKeywordLabel.id !== entity.id,
        ),
      );

      setOptions(filteredOptions);
      return;
    }

    if (areReasonSuggestionsLoading) {
      setOptions([]);
      return;
    }

    const reasonSuggestions: ReasonOrEntityLabelTypeWithGroupName[] =
      getReasonSuggestionLabels().map((label) => ({
        ...label,
        groupName: 'Suggested',
      }));

    const allReasons: ReasonOrEntityLabelTypeWithGroupName[] = reasons
      .map((reason): ReasonOrEntityLabelType => {
        return {
          id: reason.id,
          isVisible: reason.isVisible,
          category: reason.metadata?.category,
          type: FeedbackLabelType.Reason,
          createdAt: dayjs(reason.createdAt),
          displayName: reason.displayName,
        };
      })
      .map((reason) => ({
        ...reason,
        groupName: 'All Reasons',
      }));

    if (!searchTerm.trim() || isAddReasonView) {
      // filter out reason suggestions that are already added
      const filteredOptions = uniqBy(
        [...reasonSuggestions, ...allReasons].filter((reason) =>
          feedbackReasonLabelsRef.current.every(
            (reasonLabel) => reasonLabel.id !== reason.id,
          ),
        ),
        'id',
      );
      return setOptions(filteredOptions);
    } else {
      // filter out reason suggestions that are already added
      const filteredOptions = uniqBy(
        allReasons.filter((reason) =>
          feedbackReasonLabelsRef.current.every(
            (reasonLabel) => reasonLabel.id !== reason.id,
          ),
        ),
        'id',
      );
      return setOptions(filteredOptions);
    }
  }, [
    reasons,
    searchTerm,
    selectionMode,
    trackedKeywords,
    getReasonSuggestionLabels,
    isAddReasonView,
    areReasonSuggestionsLoading,
  ]);

  useDebounce(
    () => {
      if (isAddTrackedKeywordView || isAddReasonView || !isOpen) {
        return;
      }

      if (selectionMode === 'reasons') {
        fetchReasons({
          page: 1,
          searchFilter: searchTerm,
        });
      }

      if (selectionMode === 'trackedKeywords') {
        fetchTrackedKeywords({
          page: 1,
          searchFilter: searchTerm,
        });
      }
    },
    250,
    [searchTerm],
  );

  const isLoadingLabels = loadingReasons || loadingTrackedKeywords;

  const [isCreateReasonModalOpen, setIsCreateReasonModalOpen] = useState(false);
  const { showCreateNewReasonInProgressDialog, isCreateNewReasonInProgress } =
    useShowCreateNewReasonInProgressDialog({
      closeExistingCreateNewReasonDialog: () => {
        setIsCreateReasonModalOpen(false);
      },
      openCreateNewReasonDialog: () => {
        setIsCreateReasonModalOpen(true);
      },
    });

  const renderAutocompleteInput = useMemo(
    () => (params: AutocompleteRenderInputParams) =>
      (
        <Box
          ref={params.InputProps.ref}
          position="relative"
          zIndex={(theme) => theme.zIndex.modal + 1}
          bgcolor={(theme) =>
            alpha(
              theme.palette.background.paper,
              theme.palette.mode === ThemeMode.DARK ? 0 : 1, // In light mode there little shadow of the listbox falls on the button group. So, we are making the background opaque for light mode and retain paper background for dark mode
            )
          }
        >
          <TextField
            {...params}
            autoFocus
            fullWidth
            sx={{ pt: 0.75 }}
            variant="standard"
            size="small"
            onKeyDown={(event) => event.stopPropagation()}
            placeholder="Add New Prediction"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start" sx={{ pl: 1.5 }}>
                  <SearchRounded />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup
            value={selectionMode}
            fullWidth
            exclusive
            onChange={(_, value) => {
              if (!value) {
                return;
              }
              setSelectionMode(value);

              setIsAddReasonView(false);
              setIsAddTrackedKeywordView(false);

              if (value === 'reasons') {
                fetchReasons({
                  page: 1,
                  searchFilter: searchTerm,
                });
              }

              if (value === 'trackedKeywords') {
                fetchTrackedKeywords({
                  page: 1,
                  searchFilter: searchTerm,
                });
              }
            }}
            aria-label="Selection"
            size="small"
            color="primary"
          >
            <ToggleButton
              value="reasons"
              sx={{
                borderBottomLeftRadius: 0,
                borderTopLeftRadius: 0,
                borderTop: 0,
                borderRight: 0,
              }}
            >
              Reasons
            </ToggleButton>
            <ToggleButton
              value="trackedKeywords"
              sx={{
                borderBottomRightRadius: 0,
                borderTopRightRadius: 0,
                borderTop: 0,
                borderLeft: 0,
              }}
            >
              Tracked Keywords
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ),
    [fetchTrackedKeywords, searchTerm, selectionMode, fetchReasons],
  );

  const onCreateNewTrackedKeyword = useCallback(() => {
    openModal({
      contentComponent: () => (
        <AddNewTrackedKeyword
          feedbackID={feedbackID}
          name={searchTerm.trim()}
          onCreateNewTrackedKeyword={onClose}
          isDetailView={isDetailView}
        />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'lg',
        PaperProps: {
          elevation: 3,
        },
      },
    });
  }, [feedbackID, isDetailView, onClose, openModal, searchTerm]);

  const onCreateNewReason = useCallback(
    (_: SyntheticEvent, reasonName?: string) => {
      setSelectedReasonDefinition(reasonName);

      if (isCreateNewReasonInProgress) {
        showCreateNewReasonInProgressDialog();
        return;
      }

      setIsCreateReasonModalOpen(true);
      trackCreateNewReasonEvents({
        eventName: 'Create New Reason - Create New Reason Flow Triggered',
        selectedRC: reasonName,
        from: 'Feedback Record',
      });
      return;
    },
    [isCreateNewReasonInProgress, showCreateNewReasonInProgressDialog],
  );

  const onCreateLabel = useCallback(
    (event: SyntheticEvent) => {
      if (selectionMode === 'reasons') {
        onCreateNewReason(event, searchTerm);
      }

      if (selectionMode === 'trackedKeywords') {
        onCreateNewTrackedKeyword();
      }
    },
    [onCreateNewReason, onCreateNewTrackedKeyword, searchTerm, selectionMode],
  );

  const ListboxComponent = useMemo(
    () =>
      forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
        function ListboxComponent(rest, ref) {
          return (
            <Box
              role="listbox"
              ref={ref}
              {...rest}
              style={{
                paddingBottom: 0,
                maxHeight:
                  isAddTrackedKeywordView || isAddReasonView ? '100%' : '40vh',
              }}
              onScroll={(event: React.SyntheticEvent) => {
                const listboxNode = event.currentTarget;

                if (
                  isAddTrackedKeywordView ||
                  isAddReasonView ||
                  reasonPageNumberRef.current === null ||
                  trackedKeywordsPageNumberRef.current === null
                ) {
                  return;
                }

                if (
                  listboxNode.scrollTop + listboxNode.clientHeight >=
                  listboxNode.scrollHeight
                ) {
                  loadingRef.current = true;

                  if (selectionMode === 'reasons') {
                    fetchReasons({
                      page: reasonPageNumberRef.current ?? 1,
                      searchFilter: searchTerm,
                    });
                  } else {
                    fetchTrackedKeywords({
                      page: trackedKeywordsPageNumberRef.current ?? 1,
                      searchFilter: searchTerm,
                    });
                  }
                }
              }}
            >
              <Box
                component="ul"
                height="100%"
                {...rest}
                tabIndex={0}
                role="listbox"
                style={{
                  overflow: 'hidden',
                  maxHeight: 'unset',
                  paddingTop: 0,
                }}
              >
                {rest.children}

                {loadingRef.current && (
                  <Stack
                    width="100%"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <CircularProgress size={20} />
                  </Stack>
                )}
              </Box>
              {!isAddTrackedKeywordView && !isAddReasonView && (
                <Box
                  sx={{
                    position: 'sticky',
                    bottom: 0,
                    marginBottom: '0',
                  }}
                >
                  <Paper elevation={4}>
                    <CreateNewLabelButton
                      onCreateLabel={onCreateLabel}
                      selectionMode={selectionMode}
                    />
                  </Paper>
                </Box>
              )}
            </Box>
          );
        },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fetchReasons,
      fetchTrackedKeywords,
      searchTerm,
      selectionMode,
      isAddTrackedKeywordView,
      isAddReasonView,
    ],
  );

  const onAfterAddingReasonSuggestion = useCallback(
    (
      prediction: ReasonOrEntityLabelTypeWithGroupName,
      hasAddedReasonCandidate: boolean,
      addedReasonCandidates?: string[],
    ) => {
      if (prediction.type === FeedbackLabelType.Reason) {
        dispatch({
          type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
          payload: {
            id: feedbackID,
            editPredictions: [
              ...(recordInEditMode?.editPredictions ?? []),
              {
                type: FeedbackLabelType.Reason,
                manipulationType: ManipulationType.Add,
                prediction,
                isSuggested: prediction.groupName === 'Suggested',
                relatedRCs: selectedReasonDefinition
                  ? [selectedReasonDefinition]
                  : undefined,
              },
            ],
          },
        });
      }
      setIsAddReasonView(false);
      onAdditionOfReasonSuggestionProp?.();
      setSearchTerm('');
      updateEditedPredictions();
      // we need to keep the component mounted in for reason which are not suggestions until store update is done
      if (
        isReasonPartOfSuggestions(prediction, reasonSuggestions) ||
        prediction.type === FeedbackLabelType.Entity
      ) {
        onClose();
      }

      // for newly created reason, it won't have group name
      if (
        !prediction.groupName &&
        prediction.type === FeedbackLabelType.Reason
      ) {
        setShouldAddReasonToFeedback(true);
      }

      if (
        prediction.groupName ||
        prediction.type === FeedbackLabelType.Entity
      ) {
        showSuccessNotification({
          contentComponent: () => (
            <AddPredictionToast
              prediction={prediction}
              hasAddedReasonCandidate={hasAddedReasonCandidate}
              selectedReasonRC={addedReasonCandidates?.join(', ') ?? ''}
            />
          ),
          props: {},
        });
      }
    },
    [
      dispatch,
      reasonSuggestions,
      feedbackID,
      onAdditionOfReasonSuggestionProp,
      onClose,
      recordInEditMode?.editPredictions,
      selectedReasonDefinition,
      showSuccessNotification,
      updateEditedPredictions,
    ],
  );

  const onAfterAddingTrackedKeyword = (
    prediction?: ReasonOrEntityLabelType,
  ) => {
    setShouldAddTrackedKeywordToFeedback(true);
    setIsAddTrackedKeywordView(false);
    setSearchTerm('');
    showSuccessNotification({
      contentComponent: () => (
        <AddPredictionToast
          prediction={prediction}
          hasAddedReasonCandidate={false}
          selectedReasonRC={''}
        />
      ),
      props: {},
    });
  };

  useEffect(() => {
    if (shouldAddTrackedKeywordToFeedback) {
      setShouldAddTrackedKeywordToFeedback(false);
      updateEditedPredictions();
      onClose();
    }
  }, [onClose, shouldAddTrackedKeywordToFeedback, updateEditedPredictions]);

  const handleOnChange = useCallback(
    (
      _: SyntheticEvent,
      value: string | ReasonOrEntityLabelTypeWithGroupName | null,
    ) => {
      if (!value || typeof value === 'string') {
        return;
      }

      const isSelectedValueReasonCandidate =
        value.type === FeedbackLabelType.Reason && !value.category;

      if (isSelectedValueReasonCandidate) {
        onCreateNewReason(_, value.displayName);
        return;
      }

      if (value.type === FeedbackLabelType.Entity) {
        setIsAddTrackedKeywordView(true);
        return;
      }

      if (value.type === FeedbackLabelType.Reason) {
        dispatch({
          type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
          payload: {
            id: feedbackID,
            editPredictions: [
              ...(recordInEditMode?.editPredictions ?? []),
              {
                type: FeedbackLabelType.Reason,
                manipulationType: ManipulationType.Add,
                prediction: value,
              },
            ],
          },
        });

        // open add reason view if the reason is a suggestion
        if (isReasonPartOfSuggestions(value, reasonSuggestions)) {
          return setIsAddReasonView(true);
        }

        //directly add the reason if it is not a suggestion
        onAfterAddingReasonSuggestion(value, false);
        setShouldAddReasonToFeedback(true);
      }
    },
    [
      dispatch,
      feedbackID,
      onCreateNewReason,
      recordInEditMode?.editPredictions,
      onAfterAddingReasonSuggestion,
      reasonSuggestions,
    ],
  );

  useEffect(() => {
    if (shouldAddReasonToFeedback) {
      updateEditedPredictions();
      setShouldAddReasonToFeedback(false);
      onClose();
    }
  }, [shouldAddReasonToFeedback, updateEditedPredictions, onClose]);

  const onClickAwayFromAutoComplete = (event: MouseEvent | TouchEvent) => {
    if (!isOpen) {
      return;
    }

    if (
      autocompleteContainerRef.current?.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setIsAddTrackedKeywordView(false);
    setIsAddReasonView(false);
    setSearchTerm('');
    onClose();
  };

  return (
    <>
      {isOpen && (
        <ClickAwayListener onClickAway={onClickAwayFromAutoComplete}>
          <Box ref={autocompleteContainerRef}>
            <Autocomplete
              open={isOpen}
              autoComplete
              disableClearable={!searchTerm.trim()}
              value={searchTerm}
              options={options}
              loading={isLoadingLabels || areReasonSuggestionsLoading}
              groupBy={(option) =>
                searchTerm.trim() ? '' : option.groupName ?? ''
              }
              renderGroup={(params) => <AutoCompleteGroup params={params} />}
              ListboxComponent={ListboxComponent}
              PaperComponent={
                options.length === 0
                  ? (props) => (
                      <AutoCompletePaper
                        loading={isLoadingLabels}
                        selectionMode={selectionMode}
                        onCreateLabel={onCreateLabel}
                        baseProps={props}
                        areReasonSuggestionsLoading={
                          areReasonSuggestionsLoading
                        }
                      />
                    )
                  : undefined
              }
              onChange={handleOnChange}
              renderOption={(props, option, state) => (
                <AutoCompleteOption
                  props={props}
                  reasonSuggestions={reasonSuggestions}
                  option={option}
                  state={state}
                  feedbackID={feedbackID}
                  onAfterAddingTrackedKeyword={(
                    prediction?: ReasonOrEntityLabelType,
                  ) => {
                    onAfterAddingTrackedKeyword(prediction);
                  }}
                  setIsAddTrackedKeywordView={setIsAddTrackedKeywordView}
                  isAddTrackedKeywordView={isAddTrackedKeywordView}
                  isAddReasonView={isAddReasonView}
                  setIsAddReasonView={setIsAddReasonView}
                  onAfterAddingReasonSuggestion={onAfterAddingReasonSuggestion}
                  setSearchTerm={setSearchTerm}
                />
              )}
              filterOptions={(options) => options}
              disabled={isAddTrackedKeywordView || isAddReasonView}
              freeSolo
              getOptionLabel={(option) => {
                return typeof option === 'string' ? option : option.displayName;
              }}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onInputChange={(_, value) => {
                setSearchTerm(value);
              }}
              renderInput={renderAutocompleteInput}
              componentsProps={{
                paper: {
                  elevation: 8,
                  sx: {
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                  },
                },
                popper: {
                  placement: 'bottom-start',
                  modifiers: [{ name: 'flip', enabled: false }],
                  disablePortal: true,
                },
              }}
            />
          </Box>
        </ClickAwayListener>
      )}
      <CreateNewReasonV3Modal
        open={isCreateReasonModalOpen}
        onClose={() => setIsCreateReasonModalOpen(false)}
        selectedRC={
          selectedReasonDefinition
            ? { name: selectedReasonDefinition }
            : undefined
        }
        feedbackID={feedbackID}
        onCreateReason={({ reasonID, reasonName, reasonCategory }) => {
          onAfterAddingReasonSuggestion(
            {
              id: reasonID,
              isVisible: true,
              displayName: reasonName,
              category: reasonCategory,
              type: FeedbackLabelType.Reason,
            },
            true,
          );
        }}
      />
    </>
  );
};
